/**
 * Global floating language picker.
 *
 * Renders only on routes where no other language picker is on screen.
 * Hide rules (any one suppresses):
 *
 *   1. `user.email` set                   → AccountMenu's inline picker
 *                                            takes over for signed-in
 *                                            routes.
 *   2. Route in PAGES_WITH_OWN_PICKER     → that page already renders
 *                                            its own picker in-header
 *                                            (LoginPage, OnboardingWizardPage,
 *                                            HelpPage, ManualStatsPage).
 *   3. Signed-out + not on a known public  → App.js redirects signed-out
 *      route                                  users to LoginPage which has
 *                                              its own picker; rendering a
 *                                              floating one too would
 *                                              duplicate.
 *
 * The "known public" routes (rule 3) are exactly the ones App.js
 * allows past its `if (!user) return <LoginPage />;` short-circuit:
 * magic-link quote shares (`/quote/:hid?token=...`).
 */
import { useLocation } from "react-router-dom";
import LanguagePicker from "./LanguagePicker";
import useIsModalOpen from "@/lib/useIsModalOpen";

const PAGES_WITH_OWN_PICKER = [
  "/login",
  "/onboarding",
  "/help",
  "/manual-stats",
];

export default function FloatingLanguagePicker({ user }) {
  const { pathname, search } = useLocation();
  const modalOpen = useIsModalOpen();
  // Rule 1 — AccountMenu hosts the picker on signed-in routes.
  if (user?.email) return null;
  // iter150AZ.52 — Suppress while any modal is open to avoid stacked
  // floating "dialog circles".
  if (modalOpen) return null;
  // Rule 2 — pages that render their own inline picker.
  if (PAGES_WITH_OWN_PICKER.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return null;
  }
  // Rule 3 — only render on known-public signed-out routes (currently
  // just magic-link quote shares); everything else gets routed to
  // LoginPage by App.js and would duplicate.
  const isMagicLinkQuote = /^\/quote\//.test(pathname) && /[?&]token=/.test(search || "");
  if (!isMagicLinkQuote) return null;
  return (
    <div
      data-testid="floating-language-picker"
      className="fixed bottom-3 left-3 z-50 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-sm shadow-md p-1"
      role="region"
      aria-label="Language switcher"
    >
      <LanguagePicker />
    </div>
  );
}
